import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
// API
import { GENERATE_MATCHUP_INVITE_TOKEN } from 'api/matchup/mutations';
import { GenerateMatchupInviteToken } from 'api/matchup/types/GenerateMatchupInviteToken';
// Ui3
import Modal from 'ui3/Modal/Modal';
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import TextField from 'ui3/TextField/TextField';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './InviteParticipantModal.module.scss';

export type InviteParticipantSubmit = {
  name: string;
  email: string;
  inviteToken: string;
};

export type InviteParticipantModalProps = {
  open: boolean;
  onClose: () => void;
  /** Commits the invite into the competitor slot that opened the modal. */
  onSubmit: (invite: InviteParticipantSubmit) => void;
};

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
const InviteParticipantModal = ({
  open,
  onClose,
  onSubmit,
}: InviteParticipantModalProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  const [
    generateToken,
    { loading: tokenLoading },
  ] = useMutation<GenerateMatchupInviteToken>(GENERATE_MATCHUP_INVITE_TOKEN);

  useEffect(() => {
    if (!open) {
      return;
    }
    setName('');
    setEmail('');
    setInviteToken(null);
    generateToken()
      .then((result) =>
        setInviteToken(result.data?.generateMatchupInviteToken ?? null)
      )
      .catch(() => {
        showToast({
          message: 'Could not create an invite link',
          type: 'error',
          autoClose: 2000,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const emailValid = !email.trim() || EMAIL_PATTERN.test(email.trim());
  const canSubmit =
    !!name.trim() &&
    !!inviteToken &&
    !!email.trim() &&
    EMAIL_PATTERN.test(email.trim());

  const handleSubmit = () => {
    if (!canSubmit || !inviteToken) {
      return;
    }
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      inviteToken,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite Participant"
      titleAlign="left"
      size="small"
      footer={
        <Button
          variant="primary"
          size="small"
          className={styles.submit}
          disabled={!canSubmit}
          loading={tokenLoading}
          onClick={handleSubmit}
        >
          Send Invite
        </Button>
      }
    >
      <div className={styles.body}>
        <Text variant="body2Regular" color="lights-medium">
          Send an invite to onboard participants to your event.
        </Text>

        <div className={styles.field}>
          <Text variant="body3Medium" color="white">
            Participant Name
          </Text>
          <TextField
            name="invite-participant-name"
            fieldSize="small"
            placeholder="Enter Name"
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setName(event.target.value)
            }
          />
        </div>

        <div className={styles.field}>
          <Text variant="body3Medium" color="white">
            Invite via email
          </Text>
          <TextField
            name="invite-participant-email"
            type="email"
            fieldSize="small"
            prefixIcon="mail"
            placeholder="Enter participant email"
            value={email}
            error={emailValid ? undefined : 'Enter a valid email'}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default InviteParticipantModal;
